import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog, PageEvent } from '@angular/material';
import { FcPaginatorComponent } from '@core/components/fc-paginator/fc-paginator.component';
import { Group, PagedResult, Segment } from '@core/interfaces';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SegmentService } from '../../../services/segment.service';
import { LinkGroupsComponent } from './link-groups/link-groups.component';

@Component({
  selector: 'tab-groups',
  templateUrl: './tab-groups.component.html',
  styleUrls: ['./tab-groups.component.scss']
})
export class TabGroupsComponent implements OnChanges, OnDestroy, OnInit {

  @Input()
  public isViewMode: boolean;
  @Input()
  public segment: Segment;

  public searchInput = new FormControl('');
  public page: PagedResult<Group> = null;
  public isSearching = false;
  public pageSize = 5;
  public currentPage = 0;
  public isLoading = true;
  public isPageChanging = false;

  @ViewChild('paginator', { static: false })
  public paginator: FcPaginatorComponent;

  private readonly removeSubscriptions = new Subject<void>();
  private readonly reload = new Subject<void>();

  constructor(
    private readonly matDialog: MatDialog,
    private readonly segmentService: SegmentService
  ) {}

  public ngOnInit(): void {
    this.searchInput.valueChanges.pipe(debounceTime(500))
      .subscribe(() => {
        this.isLoading = true;
        if (this.paginator) {
          this.paginator.firstPage();
        } else {
          this.reload.next();
        }
      });

    this.reload.pipe(
      takeUntil(this.removeSubscriptions),
      tap(() => this.isLoading = true),
      switchMap(() => this.searchGroups()),
      tap(() => {
        this.isLoading = false;
      })
    ).subscribe((page) => {
      this.page = page;
    });

    this.reload.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.segment) {
      this.reload.next();
    }
  }

  public ngOnDestroy(): void {
    this.removeSubscriptions.next();
    this.removeSubscriptions.complete();
  }

  public openLinkLocationsDialog(): void {
    this.matDialog
      .open(LinkGroupsComponent, {
        panelClass: ['dialog-segments'],
        backdropClass: 'backdrop-transparent',
        data: {
          segment: this.segment,
        },
      })
      .beforeClosed()
      .pipe(filter((result) => result && result.reload))
      .subscribe(() => {
        this.reload.next();
      });
  }

  private async searchGroups(): Promise<PagedResult<Group>> {
    if (!this.segment.id) {
      return null;
    }

    return this.segmentService.listGroups({
      segmentId: this.segment.id,
      limit: this.pageSize,
      offset: this.currentPage * this.pageSize,
      where: {
        name_contains: this.searchInput.value,
        segmentId_in: [this.segment.id]
      }
    });
  }

  public async removeSegmentFroGroup(group: Group): Promise<void> {
    group['isSaving'] = true;
    await this.segmentService.removeSegmentFromGroup([{
      groupId: group.id,
      segmentId: this.segment.id
    }]);
    this.page = await this.searchGroups();
    group['isSaving'] = false;
  }

  async onPageChange(event: PageEvent): Promise<void> {
    this.currentPage = event.pageIndex;
    this.isPageChanging = true;
    this.page = await this.searchGroups();
    this.isPageChanging = false;
    this.isLoading = false;
  }
}
